import React, { type FC, useEffect, useState } from "react";
import type { CategoryType, PackType } from "../../../../../../../../../common-deprecated/types/CommonTypes";
import {
    getCategoryGroupIcon,
    getCategoryKey,
    getCategoryNameForCollections,
    sortCategories,
} from "../../../../../../utils/packUtils";
import Text from "../../../../../../../shared-components/css-modules/toyota/atoms/Text";
import Icon from "../../../../../../../shared-components/css-modules/toyota/atoms/Icon";
import Collapse from "../../../../../../../shared-components/css-modules/toyota/molecules/Collapse";
import styles from "./PacksAndExtras.module.scss";
import searchStyles from "../SearchBar.module.scss";
import { Pack } from "./Pack";
import { CarConfigLabel } from "../../../../../../utils/labelUtils";
import { trackOptionsToggleCategory } from "../../../../../../utils/tracking";
import { useAphPacks } from "../../../../../../hooks/useAphPacks";

export const LcvPacks: FC<{ packs: PackType[]; enabledCategoryFilters: CategoryType[] }> = ({
    packs,
    enabledCategoryFilters,
}) => {
    const categorizedPacks = useAphPacks(packs, enabledCategoryFilters).categorized;
    const [open, setOpen] = useState<string>(categorizedPacks.length ? getCategoryKey(categorizedPacks[0][0]) : "");

    useEffect(() => {
        if (categorizedPacks.length) {
            const firstSubCategoryKey = getCategoryKey(Object.entries(categorizedPacks[0][1])[0][0]);
            if (firstSubCategoryKey) {
                setOpen(`${getCategoryKey(categorizedPacks[0][0])}|${getCategoryKey(firstSubCategoryKey)}`);
            }
        }
        // Execute once
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return categorizedPacks.length ? (
        <div className={styles["info-section-wrapper"]}>
            {categorizedPacks.map(([rootCategory, categories]) => {
                const categoryGroupIcon = getCategoryGroupIcon(rootCategory);
                const rootCategoryName = getCategoryNameForCollections(rootCategory);
                const rootKey = getCategoryKey(rootCategory);
                const sortedCategories = Object.entries(categories).sort(sortCategories);
                const firstCategoryKey = getCategoryKey(sortedCategories[0][0] || rootCategory);
                return (
                    <Collapse
                        key={`extras-${rootCategory}`}
                        className={styles["category-group"]}
                        open={open.includes(rootKey)}
                        onChange={(isOpen) => {
                            trackOptionsToggleCategory(rootCategoryName || rootKey, !!isOpen);
                            setOpen(isOpen ? firstCategoryKey : "");
                        }}
                        summary={
                            <header className={styles["category-header"]}>
                                {categoryGroupIcon && (
                                    <Icon sizing="sm" variant={categoryGroupIcon} aria-hidden="true" />
                                )}
                                <Text tag="h3" variant="apheleia-h4">
                                    {rootCategoryName}
                                </Text>
                            </header>
                        }
                    >
                        {sortedCategories.map(([category, items]) => {
                            const categoryKey = getCategoryKey(category);
                            const subCategoryName = getCategoryNameForCollections(category);
                            return (
                                <div
                                    className={styles["features-list-item"]}
                                    key={`extras-${rootCategory}-${category}`}
                                >
                                    <Collapse
                                        summary={subCategoryName}
                                        open={open === `${rootKey}|${categoryKey}`}
                                        onChange={(isOpen) => {
                                            trackOptionsToggleCategory(
                                                subCategoryName || `${rootKey}|${categoryKey}`,
                                                !!isOpen,
                                            );
                                            setOpen(isOpen ? `${rootKey}|${categoryKey}` : rootKey);
                                        }}
                                    >
                                        {items.map((item, index) => (
                                            <Pack
                                                pack={item}
                                                key={`extras-${rootCategory}-${category}-${item.id}`}
                                                firstShownPack={index === 0}
                                            />
                                        ))}
                                    </Collapse>
                                </div>
                            );
                        })}
                    </Collapse>
                );
            })}
        </div>
    ) : (
        <div className={searchStyles.notfound} aria-live="assertive" aria-atomic="true">
            <CarConfigLabel label="carConfigPacksNotFound" />
        </div>
    );
};
